import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FONT_FAMILY_MAP } from '../utils';
import { useTranslation } from 'react-i18next';

// Register components for Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

const PieChartComponent = ({ widget, stats, chartType }) => {
  const { t } = useTranslation();

  const { advancedProps = {} } = widget;

  const valueFontFamily = FONT_FAMILY_MAP[advancedProps.formattingFontWeight] || '';

  // Define colors to match your original Recharts array
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const data = useMemo(() => stats ?? [], [stats]);
  const values = useMemo(() => data.map((d) => d.value ?? d.xaxis ?? 0), [data]);

  const total = useMemo(() => values.reduce((sum, v) => sum + v, 0), [values]);

  // 1. Prepare Data
  const chartData = useMemo(
    () => ({
      labels: data.map((entry) => entry.xaxis),
      datasets: [
        {
          data: values,
          backgroundColor: values.map((_, index) => COLORS[index % COLORS.length]),
          borderWidth: 1,
        },
      ],
    }),
    [data, values],
  );

  // 2. Prepare Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: 0,
    circumference: 360,
    cutout: chartType === 'pieChart' ? 0 : '60%',
    plugins: {
      legend: {
        display: true,
        position: 'right', // Aligns legend vertically on the right
        labels: {
          padding: 20,
          usePointStyle: true, // Optional: Makes legend icons circles
        },
      },
      // show value on tooltip
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `${context.label}: ${value}`;
          },
        },
      },
      // show percentage always
      datalabels: {
        formatter: (value) => {
          const percentage = total ? ((value / total) * 100).toFixed(1) : 0;

          const displayOption = advancedProps?.formattingDisplayOption;

          switch (displayOption) {
            case 'number':
              return value;

            case 'both':
              return `${value} (${percentage}%)`;

            case 'percentage':
            default:
              return `${percentage}%`;
          }
        },
        color: '#ffffff',
        backgroundColor: '#000000',
        borderRadius: 4,
        padding: {
          top: 4,
          bottom: 4,
          left: 6,
          right: 6,
        },
        font: {
          size: advancedProps.formattingFontSize,
          family: valueFontFamily,
        },
      },
    },
  };

  return !stats ? (
    <div>{t('text_loading_chart_data')}</div>
  ) : data.length === 0 ? (
    <div>No data to show</div>
  ) : (
    <div className='d-flex flex-column align-items-center'>
      <div className='d-flex flex-grow-1 w-100 p-3' style={{ width: '100%', height: '100%' }}>
        <Pie data={chartData} options={options} />
      </div>
    </div>
  );
};

PieChartComponent.propTypes = {
  widget: PropTypes.object.isRequired,
  stats: PropTypes.array,
  chartType: PropTypes.string.isRequired,
};

export default PieChartComponent;
